/**
 * legacyCopy entries for the AUTOMATION family — ported verbatim (including
 * existing quirks/bugs) from `SuggestedActionForError` (howToFixIt) and
 * `getActionErrorDetailsText` (whatCausedIt) in `../../ParticipantDialog.tsx`.
 *
 * The two original cascades don't always cover the same set of branches for
 * a given action type — where one has no matching branch, its function
 * below returns `null`, exactly matching the original's fallthrough. See
 * inline comments on the handful of intentionally-preserved asymmetries.
 */
import { FileResourceType } from '@dadohr/shared/domainTypes/experience';
import { Link } from '@material-ui/core';
import { capitalize } from 'lodash';
import moment from 'moment-timezone';
import React, { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { LinkExternal } from '../../../../../../components/LinkExternal';
import { refreshTimestamp } from '../../../../../../services/firebase';
import { LightMdToJsx } from '../../../../../../services/formatter';
import { toMoment } from '../../../../../../services/time';
import { getAdditionalInfoText } from '../../getAdditionalInfoText';
import { defaultHumanReadableActorName } from '../textHelpers';
import { AutomationIssueRaw, CopyPack, Issue, IssueKind } from '../types';

const rawOf = (issue: Issue) => issue.raw as AutomationIssueRaw;
// `AutomationActionDenormalized` is a union of 13 variants, not all of which
// carry typed `errorData`/`channel`/`templateId`-adjacent fields, so TS won't
// allow accessing those across the whole union without narrowing. Neither
// cascade ever handled Asana actions (see resolveAutomationIssueKind's default
// case), so `any` here is exactly as safe as the original code's unchecked
// `a.errorData` access.
const actionOf = (group: Issue[]): any => rawOf(group[0]).action;
const instanceOf = (group: Issue[]) => rawOf(group[0]).instance;
const ctxOf = (group: Issue[]) => group[0].ctx;
const errData = (group: Issue[]): any => actionOf(group).errorData;
/** Which document platform a create-document action copies from. */
const documentProviderName = (group: Issue[]): string =>
  actionOf(group).srcTemplate?.type === FileResourceType.MS_ONE_DRIVE ? 'Microsoft' : 'Google';

const humanReadableActorName = (group: Issue[], actorKey: string): string => {
  const c = ctxOf(group);
  return (c.humanReadableActorName || defaultHumanReadableActorName)(actorKey, c.supportingActors);
};

const getAnchorFields = (
  group: Issue[],
  customFields: import('@dadohr/shared/domainTypes/org').CustomUserField[]
) => ctxOf(group).getAnchorFields?.(customFields) || [];

const participantLink = (group: Issue[]): ReactNode => {
  const inst = instanceOf(group);
  const c = ctxOf(group);
  if (!inst || !c.onOpenParticipant) return null;
  return (
    <div>
      <LightMdToJsx>{c.t('Add this supporting actor to the ')}</LightMdToJsx>
      <Link onClick={() => c.onOpenParticipant?.(inst)}>{c.t(`Participant's experience`)}</Link>
    </div>
  );
};

const md = (text: string | null): ReactNode =>
  text ? <LightMdToJsx>{text}</LightMdToJsx> : null;

const atlassianRejectedFieldsText = (group: Issue[]): string => {
  const c = ctxOf(group);
  const t = c.t;
  const ed = errData(group);
  const fieldErrors = (ed?.fieldErrors || []) as { field: string; message: string }[];
  const errorMessages = (ed?.errorMessages || []) as string[];
  const missingFields = (ed?.missingFields || []) as string[];
  const details = [
    ...fieldErrors.map((f) => f.message),
    ...errorMessages,
    ...missingFields.map((f) => t('**{{field}}** is required', { field: f }))
  ].filter(Boolean);
  if (details.length) {
    return t('Couldn’t create Jira ticket – Jira rejected: {{details}}', {
      details: details.join('; ')
    });
  }
  return t('Unknown error creating Jira ticket');
};

const calendarNoEmailWhatCausedIt = (group: Issue[]): string => {
  const c = ctxOf(group);
  const t = c.t;
  const ed = errData(group);
  const actorKey = ed?.data?.actorKey;
  const actor = humanReadableActorName(group, actorKey);
  if (ed?.data?.emailField === 'email')
    return t(`Couldn't send invite - **{{actor}}** missing **Work Email**`, { actor });
  if (ed?.data?.emailField === 'personalEmail')
    return t(`Couldn't send invite - **{{actor}}** missing **Personal Email**`, { actor });
  return t(`Couldn't send invite - **{{actor}}** missing **Email**`, { actor });
};

const calendarNoEmailHowToFixIt = (group: Issue[]): ReactNode =>
  md(ctxOf(group).t('Add **{{ addressType }}** to import source and run a new import', {
    addressType: 'Work Email'
  }));

const calendarNoActorWhatCausedIt = (group: Issue[]): string => {
  const c = ctxOf(group);
  const ed = errData(group);
  const actorKey = ed?.data?.actorKey;
  return c.t(
    `Could not add **{{ actor }}** to event - **{{ actor }}** not setup for this experience`,
    { actor: c.supportingActors?.[actorKey]?.name || actorKey }
  );
};

const slackChannelName = (group: Issue[]): string => {
  const action = actionOf(group);
  const ed = errData(group);
  return (
    ed?.data?.channel?.name ||
    (action.channel.type === 'EXISTING' ? action.channel.name : '') ||
    ''
  );
};

export const automationLegacyCopy: Partial<CopyPack> = {
  // ---- ATLASSIAN_CREATE_ISSUE ----
  [IssueKind.ATLASSIAN_CREATE_ISSUE__INSUFFICIENT_PERMISSION]: {
    category: 'Automation: Jira issue creation',
    summary: (g) => ctxOf(g).t('Cannot connect to Jira'),
    whatCausedIt: (g) => ctxOf(g).t('Cannot connect to Jira'),
    howToFixIt: (g) =>
      md(ctxOf(g).t('Ensure the integration account has sufficient authorization in Jira'))
  },
  [IssueKind.ATLASSIAN_CREATE_ISSUE__ACTOR_WITHOUT_EMAIL]: {
    category: 'Automation: Jira issue creation',
    summary: (g) =>
      ctxOf(g).t('Couldn’t add **{{actorName}}** to Jira ticket – missing **work email**', {
        actorName: errData(g).actorName
      }),
    whatCausedIt: (g) =>
      ctxOf(g).t('Couldn’t add **{{actorName}}** to Jira ticket – missing **work email**', {
        actorName: errData(g).actorName
      }),
    howToFixIt: (g) => md(ctxOf(g).t('Add **Work Email** to import source and run a new import'))
  },
  [IssueKind.ATLASSIAN_CREATE_ISSUE__NO_JIRA_USER]: {
    category: 'Automation: Jira issue creation',
    summary: (g) =>
      ctxOf(g).t(
        'Couldn’t add **{{fieldName}}** to Jira ticket – no Jira user with email **{{actorEmail}}**',
        { fieldName: errData(g).fieldName, actorEmail: errData(g).actorEmail || errData(g).reporterEmail }
      ),
    whatCausedIt: (g) =>
      ctxOf(g).t(
        'Couldn’t add **{{fieldName}}** to Jira ticket – no Jira user with email **{{actorEmail}}**',
        { fieldName: errData(g).fieldName, actorEmail: errData(g).actorEmail || errData(g).reporterEmail }
      ),
    howToFixIt: (g) => md(ctxOf(g).t('Create Jira user or contact Support to update email in Dado'))
  },
  [IssueKind.ATLASSIAN_CREATE_ISSUE__FIELDS_ERROR]: {
    category: 'Automation: Jira issue creation',
    summary: atlassianRejectedFieldsText,
    whatCausedIt: atlassianRejectedFieldsText,
    howToFixIt: (g) =>
      md(ctxOf(g).t('Correct the rejected field(s) in the Jira action template and run a new import'))
  },
  [IssueKind.ATLASSIAN_CREATE_ISSUE__API_ERROR]: {
    category: 'Automation: Jira issue creation',
    summary: atlassianRejectedFieldsText,
    whatCausedIt: atlassianRejectedFieldsText,
    howToFixIt: (g) => md(ctxOf(g).t('Contact Dado Support'))
  },
  [IssueKind.ATLASSIAN_CREATE_ISSUE__DEFAULT]: {
    category: 'Automation: Jira issue creation',
    summary: (g) => ctxOf(g).t('Unknown error creating Jira ticket'),
    whatCausedIt: (g) => ctxOf(g).t('Unknown error creating Jira ticket'),
    howToFixIt: (g) => md(ctxOf(g).t('Contact Dado Support'))
  },

  // ---- ENROLL_IN_EXPERIENCE ----
  [IssueKind.ENROLL_IN_EXPERIENCE__CRITERIA_NOT_MET]: {
    category: 'Automation: Enrollment',
    summary: (g) =>
      ctxOf(g).t(`Can't enroll in **{{ template }}** - enrollment criteria not met`, {
        template: errData(g)?.data?.template?.name
      }),
    whatCausedIt: (g) =>
      ctxOf(g).t(`Can't enroll in **{{ template }}** - enrollment criteria not met`, {
        template: errData(g)?.data?.template?.name
      }),
    howToFixIt: (g) => {
      const c = ctxOf(g);
      const ed = errData(g);
      const templateId = ed?.data?.template?.id;
      return (
        <div>
          <LightMdToJsx>{c.t('Update user data or amend')}</LightMdToJsx>{' '}
          {templateId && c.routes ? (
            <LinkExternal
              color="primary"
              href={c.routes.experiences.details.edit.url(templateId, { step: 'participants' })}
            >
              {c.t('criteria')}
            </LinkExternal>
          ) : (
            c.t('criteria')
          )}
        </div>
      );
    }
  },
  [IssueKind.ENROLL_IN_EXPERIENCE__NO_ANCHOR]: {
    category: 'Automation: Enrollment',
    summary: (g) => {
      const c = ctxOf(g);
      const ed = errData(g);
      const anchorField = ed?.data?.anchor?.field;
      return c.t(`Can't enroll in **{{ template }}** - no {{ anchor }}`, {
        template: ed?.data?.template?.name,
        anchor:
          (c.customUserFields &&
            getAnchorFields(g, c.customUserFields).find((x) => x.field === anchorField)?.shortLabel(c.t)) ||
          c.t('milestone')
      });
    },
    whatCausedIt: (g) => automationLegacyCopy[IssueKind.ENROLL_IN_EXPERIENCE__NO_ANCHOR]!.summary(g),
    howToFixIt: (g) => md(ctxOf(g).t('Update user data via import or manually enroll'))
  },
  [IssueKind.ENROLL_IN_EXPERIENCE__EXPERIENCE_STOPPED]: {
    category: 'Automation: Enrollment',
    summary: (g) =>
      ctxOf(g).t(`Can't enroll in **{{ template }}** - experience is not active`, {
        template: errData(g)?.data?.template?.name
      }),
    whatCausedIt: (g) =>
      ctxOf(g).t(`Can't enroll in **{{ template }}** - experience is not active`, {
        template: errData(g)?.data?.template?.name
      }),
    howToFixIt: (g) => md(ctxOf(g).t('Start experience or update automation'))
  },
  [IssueKind.ENROLL_IN_EXPERIENCE__EXPERIENCE_ARCHIVED]: {
    category: 'Automation: Enrollment',
    summary: (g) =>
      ctxOf(g).t(`Can't enroll in **{{ template }}** - experience archived`, {
        template: errData(g)?.data?.template?.name
      }),
    whatCausedIt: (g) =>
      ctxOf(g).t(`Can't enroll in **{{ template }}** - experience archived`, {
        template: errData(g)?.data?.template?.name
      }),
    howToFixIt: (g) => md(ctxOf(g).t('Unarchive experience or update automation'))
  },
  [IssueKind.ENROLL_IN_EXPERIENCE__ALREADY_ENROLLED]: {
    category: 'Automation: Enrollment',
    summary: (g) =>
      ctxOf(g).t(`Can't enroll in **{{ template }}** - already enrolled`, {
        template: errData(g)?.data?.template?.name
      }),
    whatCausedIt: (g) =>
      ctxOf(g).t(`Can't enroll in **{{ template }}** - already enrolled`, {
        template: errData(g)?.data?.template?.name
      }),
    howToFixIt: (g) =>
      md(ctxOf(g).t('Remove them from the experience, if a new enrollment is needed'))
  },
  [IssueKind.ENROLL_IN_EXPERIENCE__TEMPLATE_NOT_FOUND]: {
    category: 'Automation: Enrollment',
    summary: (g) => ctxOf(g).t(`Can't enroll - experience not found`),
    whatCausedIt: (g) => ctxOf(g).t(`Can't enroll - experience not found`),
    howToFixIt: (g) => md(ctxOf(g).t('Update automation'))
  },
  [IssueKind.ENROLL_IN_EXPERIENCE__TOO_FAR_IN_THE_PAST]: {
    category: 'Automation: Enrollment',
    summary: (g) => {
      const c = ctxOf(g);
      const ed = errData(g);
      const startDate = refreshTimestamp(ed?.data?.startDate || null);
      const diffDays = startDate ? Math.abs(toMoment(startDate).diff(moment(), 'day')) : null;
      return c.t(`Can't enroll in **{{ template }}** - would enroll {{ gap }} late`, {
        name: ed?.data?.participant?.fullName,
        template: ed?.data?.template?.name,
        gap: diffDays === null ? c.t('too') : c.t('{{ days }} days', { days: diffDays })
      });
    },
    whatCausedIt: (g) =>
      automationLegacyCopy[IssueKind.ENROLL_IN_EXPERIENCE__TOO_FAR_IN_THE_PAST]!.summary(g),
    howToFixIt: (g) => {
      const c = ctxOf(g);
      const ed = errData(g);
      return md(
        c.t('Change {{ anchor }} via import or enroll manually', {
          anchor:
            getAnchorFields(g, c.customUserFields || [])
              .find((x) => x.field === ed?.data?.anchor?.field)
              ?.shortLabel(c.t) || c.t('milestone')
        })
      );
    }
  },
  [IssueKind.ENROLL_IN_EXPERIENCE__ARCHIVE_FAILED]: {
    category: 'Automation: Enrollment',
    summary: (g) => ctxOf(g).t('Failed to archive the current experience before re-enrolling'),
    whatCausedIt: (g) => ctxOf(g).t('Failed to archive the current experience before re-enrolling'),
    // No dedicated branch in SuggestedActionForError — falls to this block's default.
    howToFixIt: (g) => md(ctxOf(g).t('Contact Dado Support'))
  },
  [IssueKind.ENROLL_IN_EXPERIENCE__DEFAULT]: {
    category: 'Automation: Enrollment',
    summary: (g) => ctxOf(g).t('Unknown error'),
    whatCausedIt: (g) => ctxOf(g).t('Unknown error'),
    howToFixIt: (g) => md(ctxOf(g).t('Contact Dado Support'))
  },

  // ---- GOOGLE_ADD_TO_GROUP ----
  [IssueKind.GOOGLE_ADD_TO_GROUP__NO_ACTOR]: {
    category: 'Automation: Google group membership',
    summary: (g) =>
      ctxOf(g).t(`Couldn't add to group - **{{ actor }}** not set for this experience`, {
        actor: capitalize(ctxOf(g).supportingActors?.[errData(g).data?.actorKey]?.name || errData(g).data?.actorKey)
      }),
    whatCausedIt: (g) =>
      automationLegacyCopy[IssueKind.GOOGLE_ADD_TO_GROUP__NO_ACTOR]!.summary(g),
    howToFixIt: participantLink
  },
  [IssueKind.GOOGLE_ADD_TO_GROUP__NO_EMAIL]: {
    category: 'Automation: Google group membership',
    summary: (g) => {
      const c = ctxOf(g);
      const ed = errData(g);
      const actor = humanReadableActorName(g, ed?.data?.actorKey);
      if (ed?.data?.emailField === 'email')
        return c.t(`Couldn't add to group - **{{actor}}** missing **Work Email**`, { actor });
      if (ed?.data?.emailField === 'personalEmail')
        return c.t(`Couldn't add to group - **{{actor}}** missing **Personal Email**`, { actor });
      return c.t('Unknown error');
    },
    whatCausedIt: (g) => automationLegacyCopy[IssueKind.GOOGLE_ADD_TO_GROUP__NO_EMAIL]!.summary(g),
    howToFixIt: (g) => {
      const ed = errData(g);
      if (ed?.data?.emailField === 'email')
        return md(
          ctxOf(g).t('Add **{{ addressType }}** to import source and run a new import', {
            addressType: 'Work Email'
          })
        );
      if (ed?.data?.emailField === 'personalEmail')
        return md(
          ctxOf(g).t('Add **{{ addressType }}** to import source and run a new import', {
            addressType: 'Personal Email'
          })
        );
      return null;
    }
  },
  [IssueKind.GOOGLE_ADD_TO_GROUP__API_ERROR]: {
    category: 'Automation: Google group membership',
    summary: (g) => ctxOf(g).t('Google Groups API Error'),
    whatCausedIt: (g) => ctxOf(g).t('Google Groups API Error'),
    howToFixIt: () => null
  },
  [IssueKind.GOOGLE_ADD_TO_GROUP__DEFAULT]: {
    category: 'Automation: Google group membership',
    summary: (g) => ctxOf(g).t('Unknown error'),
    whatCausedIt: (g) => ctxOf(g).t('Unknown error'),
    howToFixIt: () => null
  },

  // ---- OKTA_ADD_TO_GROUP ----
  [IssueKind.OKTA_ADD_TO_GROUP__NO_ACTOR]: {
    category: 'Automation: Okta group membership',
    summary: (g) =>
      ctxOf(g).t(`Couldn't add to group - **{{ actor }}** not set for this experience`, {
        actor: capitalize(ctxOf(g).supportingActors?.[errData(g).data?.actorKey]?.name || errData(g).data?.actorKey)
      }),
    whatCausedIt: (g) => automationLegacyCopy[IssueKind.OKTA_ADD_TO_GROUP__NO_ACTOR]!.summary(g),
    howToFixIt: participantLink
  },
  [IssueKind.OKTA_ADD_TO_GROUP__NO_EMAIL]: {
    category: 'Automation: Okta group membership',
    summary: (g) => {
      const c = ctxOf(g);
      const ed = errData(g);
      const actor = humanReadableActorName(g, ed?.data?.actorKey);
      if (ed?.data?.emailField === 'email')
        return c.t(`Couldn't add to group - **{{actor}}** missing **Work Email**`, { actor });
      if (ed?.data?.emailField === 'personalEmail')
        return c.t(`Couldn't add to group - **{{actor}}** missing **Personal Email**`, { actor });
      return c.t('Unknown error');
    },
    whatCausedIt: (g) => automationLegacyCopy[IssueKind.OKTA_ADD_TO_GROUP__NO_EMAIL]!.summary(g),
    howToFixIt: (g) => {
      const ed = errData(g);
      if (ed?.data?.emailField === 'email')
        return md(
          ctxOf(g).t('Add **{{ addressType }}** to import source and run a new import', {
            addressType: 'Work Email'
          })
        );
      if (ed?.data?.emailField === 'personalEmail')
        return md(
          ctxOf(g).t('Add **{{ addressType }}** to import source and run a new import', {
            addressType: 'Personal Email'
          })
        );
      return null;
    }
  },
  [IssueKind.OKTA_ADD_TO_GROUP__NO_OKTA_USER]: {
    category: 'Automation: Okta group membership',
    summary: (g) =>
      ctxOf(g).t('No Okta user with email **{{ email }}** found', {
        email: errData(g).data.emailAddress || ''
      }),
    whatCausedIt: (g) => automationLegacyCopy[IssueKind.OKTA_ADD_TO_GROUP__NO_OKTA_USER]!.summary(g),
    howToFixIt: (g) => md(ctxOf(g).t('Add this user to your Okta organization'))
  },
  [IssueKind.OKTA_ADD_TO_GROUP__NO_GROUP]: {
    category: 'Automation: Okta group membership',
    summary: (g) =>
      ctxOf(g).t(`Couldn't find group **{{ groupName }}** in Okta`, {
        groupName: errData(g)?.data?.group?.name || 'Unknown'
      }),
    whatCausedIt: (g) => automationLegacyCopy[IssueKind.OKTA_ADD_TO_GROUP__NO_GROUP]!.summary(g),
    howToFixIt: (g) => {
      const c = ctxOf(g);
      const inst = instanceOf(g);
      const action = actionOf(g);
      if (!inst) return <div>{c.t('Update group in')} {c.t('automation')}</div>;
      let autTplId: string | null = null;
      Object.entries(inst.data.automations).find(([_autTplId, aut]: [string, any]) => {
        if (aut.actions[action.templateId]) {
          autTplId = _autTplId;
          return true;
        }
        return false;
      });
      return (
        <div>
          {c.t('Update group in')}{' '}
          {autTplId && c.routes ? (
            <RouterLink
              to={c.routes.experiences.details.edit.url(inst.data.templateId, {
                step: 'tasks',
                autommationId: autTplId
              } as any)}
            >
              {c.t('automation')}
            </RouterLink>
          ) : (
            c.t('automation')
          )}
        </div>
      );
    }
  },
  [IssueKind.OKTA_ADD_TO_GROUP__PERMISSION_DENIED]: {
    category: 'Automation: Okta group membership',
    summary: (g) => ctxOf(g).t('Okta integration permission denied'),
    whatCausedIt: (g) => ctxOf(g).t('Okta integration permission denied'),
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <div>
          {c.t('Check you granted')}{' '}
          <LinkExternal
            color="primary"
            href="https://help.dadohr.com/article/117-okta-integrations#Grant-the-correct-API-scopes-1e9qe"
          >
            {c.t('correct scopes')}
          </LinkExternal>{' '}
          {c.t('in Okta interface')}
        </div>
      );
    }
  },
  [IssueKind.OKTA_ADD_TO_GROUP__API_ERROR]: {
    category: 'Automation: Okta group membership',
    summary: (g) => ctxOf(g).t('Error communicating with Okta'),
    whatCausedIt: (g) => ctxOf(g).t('Error communicating with Okta'),
    howToFixIt: (g) => md(ctxOf(g).t('Retry after a few minutes'))
  },
  [IssueKind.OKTA_ADD_TO_GROUP__DEFAULT]: {
    category: 'Automation: Okta group membership',
    summary: (g) => ctxOf(g).t('Unknown error'),
    whatCausedIt: (g) => ctxOf(g).t('Unknown error'),
    howToFixIt: () => null
  },

  // ---- GOOGLE_CALENDAR_EVENT_INVITATION ----
  [IssueKind.GOOGLE_CALENDAR_EVENT_INVITATION__NO_EVENT_FOUND]: {
    category: 'Automation: Google Calendar invitation',
    summary: (g) => {
      const c = ctxOf(g);
      const additionalInfo = getAdditionalInfoText(actionOf(g), c.t as any);
      return c.t(`Could not find event **{{ additionalInfo }}**`, { additionalInfo });
    },
    whatCausedIt: (g) =>
      automationLegacyCopy[IssueKind.GOOGLE_CALENDAR_EVENT_INVITATION__NO_EVENT_FOUND]!.summary(g),
    howToFixIt: (g) =>
      md(ctxOf(g).t(`Check event name is correct in Dado and event exists in calendar`))
  },
  [IssueKind.GOOGLE_CALENDAR_EVENT_INVITATION__NO_EMAIL]: {
    category: 'Automation: Google Calendar invitation',
    summary: calendarNoEmailWhatCausedIt,
    whatCausedIt: calendarNoEmailWhatCausedIt,
    howToFixIt: calendarNoEmailHowToFixIt
  },
  [IssueKind.GOOGLE_CALENDAR_EVENT_INVITATION__API_ERROR]: {
    category: 'Automation: Google Calendar invitation',
    summary: (g) => ctxOf(g).t('Error communicating with Google Calendar'),
    whatCausedIt: (g) => ctxOf(g).t('Error communicating with Google Calendar'),
    howToFixIt: (g) => md(ctxOf(g).t('Retry after a few minutes'))
  },
  [IssueKind.GOOGLE_CALENDAR_EVENT_INVITATION__NO_ACTOR]: {
    category: 'Automation: Google Calendar invitation',
    summary: calendarNoActorWhatCausedIt,
    whatCausedIt: calendarNoActorWhatCausedIt,
    howToFixIt: participantLink
  },

  // ---- MS_CALENDAR_EVENT_INVITATION ----
  [IssueKind.MS_CALENDAR_EVENT_INVITATION__NO_EVENT_FOUND]: {
    category: 'Automation: Microsoft Calendar invitation',
    summary: (g) => {
      const c = ctxOf(g);
      const additionalInfo = getAdditionalInfoText(actionOf(g), c.t as any);
      return c.t(`Could not find event **{{ additionalInfo }}**`, { additionalInfo });
    },
    whatCausedIt: (g) =>
      automationLegacyCopy[IssueKind.MS_CALENDAR_EVENT_INVITATION__NO_EVENT_FOUND]!.summary(g),
    howToFixIt: (g) =>
      md(ctxOf(g).t(`Check event name is correct in Dado and event exists in calendar`))
  },
  [IssueKind.MS_CALENDAR_EVENT_INVITATION__NO_EMAIL]: {
    category: 'Automation: Microsoft Calendar invitation',
    summary: calendarNoEmailWhatCausedIt,
    whatCausedIt: calendarNoEmailWhatCausedIt,
    howToFixIt: calendarNoEmailHowToFixIt
  },
  [IssueKind.MS_CALENDAR_EVENT_INVITATION__API_ERROR]: {
    category: 'Automation: Microsoft Calendar invitation',
    summary: (g) => ctxOf(g).t('Error communicating with Microsoft Calendar'),
    whatCausedIt: (g) => ctxOf(g).t('Error communicating with Microsoft Calendar'),
    howToFixIt: (g) => md(ctxOf(g).t('Retry after a few minutes'))
  },
  [IssueKind.MS_CALENDAR_EVENT_INVITATION__NO_ACTOR]: {
    category: 'Automation: Microsoft Calendar invitation',
    summary: calendarNoActorWhatCausedIt,
    whatCausedIt: calendarNoActorWhatCausedIt,
    howToFixIt: participantLink
  },

  // ---- MS_ADD_TO_GROUP ----
  [IssueKind.MS_ADD_TO_GROUP__NO_ACTOR]: {
    category: 'Automation: Microsoft group/team membership',
    summary: (g) =>
      ctxOf(g).t(`Couldn't add to group/team - **{{ actor }}** not set for this experience`, {
        actor: capitalize(ctxOf(g).supportingActors?.[errData(g).data?.actorKey]?.name || errData(g).data?.actorKey)
      }),
    whatCausedIt: (g) => automationLegacyCopy[IssueKind.MS_ADD_TO_GROUP__NO_ACTOR]!.summary(g),
    howToFixIt: participantLink
  },
  [IssueKind.MS_ADD_TO_GROUP__NO_EMAIL]: {
    category: 'Automation: Microsoft group/team membership',
    summary: (g) => {
      const c = ctxOf(g);
      const ed = errData(g);
      const actor = humanReadableActorName(g, ed?.data?.actorKey);
      if (ed?.data?.emailField === 'email')
        return c.t(`Couldn't add to group/team - **{{actor}}** missing **Work Email**`, { actor });
      if (ed?.data?.emailField === 'personalEmail')
        return c.t(`Couldn't add to group/team - **{{actor}}** missing **Personal Email**`, {
          actor
        });
      return c.t('Unknown error');
    },
    whatCausedIt: (g) => automationLegacyCopy[IssueKind.MS_ADD_TO_GROUP__NO_EMAIL]!.summary(g),
    howToFixIt: (g) => {
      const ed = errData(g);
      if (ed?.data?.emailField === 'email')
        return md(
          ctxOf(g).t('Add **{{ addressType }}** to import source and run a new import', {
            addressType: 'Work Email'
          })
        );
      if (ed?.data?.emailField === 'personalEmail')
        return md(
          ctxOf(g).t('Add **{{ addressType }}** to import source and run a new import', {
            addressType: 'Personal Email'
          })
        );
      return null;
    }
  },
  [IssueKind.MS_ADD_TO_GROUP__NO_MICROSOFT_USER]: {
    category: 'Automation: Microsoft group/team membership',
    summary: (g) =>
      ctxOf(g).t(`Couldn't find Microsoft user with email **{{email}}**`, {
        email: errData(g)?.data.email
      }),
    whatCausedIt: (g) =>
      automationLegacyCopy[IssueKind.MS_ADD_TO_GROUP__NO_MICROSOFT_USER]!.summary(g),
    howToFixIt: (g) => md(ctxOf(g).t('Add this user to your Microsoft organization'))
  },
  [IssueKind.MS_ADD_TO_GROUP__API_ERROR]: {
    category: 'Automation: Microsoft group/team membership',
    summary: (g) => ctxOf(g).t('Microsoft API Error'),
    whatCausedIt: (g) => ctxOf(g).t('Microsoft API Error'),
    howToFixIt: () => null
  },
  [IssueKind.MS_ADD_TO_GROUP__DEFAULT]: {
    category: 'Automation: Microsoft group/team membership',
    summary: (g) => ctxOf(g).t('Unknown error'),
    whatCausedIt: (g) => ctxOf(g).t('Unknown error'),
    howToFixIt: () => null
  },

  // ---- MS_TEAMS_INVITE_TO_CHANNEL ----
  [IssueKind.MS_TEAMS_INVITE_TO_CHANNEL__NO_ACTOR]: {
    category: 'Automation: Teams channel invitation',
    summary: (g) =>
      ctxOf(g).t(`Couldn't invite user - missing supporting actor **{{ actor }}**`, {
        actor: capitalize(ctxOf(g).supportingActors?.[errData(g).data?.actorKey]?.name || errData(g).data?.actorKey)
      }),
    whatCausedIt: (g) =>
      automationLegacyCopy[IssueKind.MS_TEAMS_INVITE_TO_CHANNEL__NO_ACTOR]!.summary(g),
    howToFixIt: participantLink
  },
  [IssueKind.MS_TEAMS_INVITE_TO_CHANNEL__NO_EMAIL]: {
    category: 'Automation: Teams channel invitation',
    summary: (g) => {
      const c = ctxOf(g);
      const ed = errData(g);
      if (ed?.data?.actionActor?.emailField === 'email')
        return c.t(`Couldn't find Microsoft user - **{{actor}}** missing **Work Email**`, {
          actor: humanReadableActorName(g, ed.data.actionActor.key)
        });
      if (ed?.data?.actionActor?.emailField === 'personalEmail')
        return c.t(`Couldn't find Microsoft user - **{{actor}}** missing **Personal Email**`, {
          actor: humanReadableActorName(g, ed.data.actionActor.key)
        });
      return c.t('Missing **Email**');
    },
    whatCausedIt: (g) =>
      automationLegacyCopy[IssueKind.MS_TEAMS_INVITE_TO_CHANNEL__NO_EMAIL]!.summary(g),
    howToFixIt: (g) =>
      md(
        ctxOf(g).t('Add **{{ addressType }}** to import source and run a new import', {
          addressType: 'Work Email'
        })
      )
  },
  [IssueKind.MS_TEAMS_INVITE_TO_CHANNEL__CHANNEL_NOT_FOUND]: {
    category: 'Automation: Teams channel invitation',
    summary: (g) =>
      ctxOf(g).t('Couldn’t find channel **{{channelName}}**', {
        channelName: errData(g)?.data?.channel?.name
      }),
    whatCausedIt: (g) =>
      automationLegacyCopy[IssueKind.MS_TEAMS_INVITE_TO_CHANNEL__CHANNEL_NOT_FOUND]!.summary(g),
    howToFixIt: (g) => md(ctxOf(g).t('Make sure the channel exists'))
  },
  [IssueKind.MS_TEAMS_INVITE_TO_CHANNEL__USER_NOT_IN_TEAM]: {
    category: 'Automation: Teams channel invitation',
    summary: (g) =>
      ctxOf(g).t('User isn’t in team **{{teamName}}**', { teamName: errData(g)?.data?.team.name }),
    whatCausedIt: (g) =>
      automationLegacyCopy[IssueKind.MS_TEAMS_INVITE_TO_CHANNEL__USER_NOT_IN_TEAM]!.summary(g),
    // Faithful port of an existing bug: the JSX side interpolates the raw
    // `data.team` value (not `data.team.name`) into the message.
    howToFixIt: (g) =>
      md(
        ctxOf(g).t('Make sure the user is member of the team {{teamName}}', {
          teamName: errData(g)?.data?.team
        })
      )
  },
  [IssueKind.MS_TEAMS_INVITE_TO_CHANNEL__NO_MICROSOFT_USER]: {
    category: 'Automation: Teams channel invitation',
    summary: (g) =>
      ctxOf(g).t('Couldn’t find Microsoft user with email **{{email}}**', {
        email: errData(g)?.data?.email
      }),
    whatCausedIt: (g) =>
      automationLegacyCopy[IssueKind.MS_TEAMS_INVITE_TO_CHANNEL__NO_MICROSOFT_USER]!.summary(g),
    howToFixIt: (g) => md(ctxOf(g).t('Create Microsoft user with this email address'))
  },
  [IssueKind.MS_TEAMS_INVITE_TO_CHANNEL__DEFAULT]: {
    category: 'Automation: Teams channel invitation',
    summary: (g) => ctxOf(g).t('Unknown error'),
    whatCausedIt: (g) => ctxOf(g).t('Unknown error'),
    howToFixIt: () => null
  },

  // ---- SLACK_INVITE_TO_CHANNEL ----
  [IssueKind.SLACK_INVITE_TO_CHANNEL__NO_EMAIL]: {
    category: 'Automation: Slack channel invitation',
    summary: (g) => {
      const c = ctxOf(g);
      const ed = errData(g);
      if (ed?.data?.actionActor?.emailField === 'email')
        return c.t(`Couldn't find Slack user - **{{actor}}** missing **Work Email**`, {
          actor: humanReadableActorName(g, ed.data.actionActor.key)
        });
      if (ed?.data?.actionActor?.emailField === 'personalEmail')
        return c.t(`Couldn't find Slack user - **{{actor}}** missing **Personal Email**`, {
          actor: humanReadableActorName(g, ed.data.actionActor.key)
        });
      return c.t('Missing **Email**');
    },
    whatCausedIt: (g) => automationLegacyCopy[IssueKind.SLACK_INVITE_TO_CHANNEL__NO_EMAIL]!.summary(g),
    howToFixIt: (g) =>
      md(
        ctxOf(g).t('Add **{{ addressType }}** to import source and run a new import', {
          addressType: 'Work Email'
        })
      )
  },
  [IssueKind.SLACK_INVITE_TO_CHANNEL__NO_SLACK_USER]: {
    category: 'Automation: Slack channel invitation',
    summary: (g) => {
      const c = ctxOf(g);
      const ed = errData(g);
      if (ed?.data?.email)
        return c.t(`Couldn't find Slack user with the email **{{ email }}**`, {
          email: ed.data.email
        });
      return c.t('Could not find user in your Slack workspace');
    },
    whatCausedIt: (g) =>
      automationLegacyCopy[IssueKind.SLACK_INVITE_TO_CHANNEL__NO_SLACK_USER]!.summary(g),
    howToFixIt: (g) => md(ctxOf(g).t('Create Slack user with this email address'))
  },
  [IssueKind.SLACK_INVITE_TO_CHANNEL__NOT_IN_CHANNEL]: {
    category: 'Automation: Slack channel invitation',
    summary: (g) =>
      ctxOf(g).t(`Couldn't invite user because the Dado bot is not in **{{ channel }}** channel`, {
        channel: slackChannelName(g)
      }),
    whatCausedIt: (g) =>
      automationLegacyCopy[IssueKind.SLACK_INVITE_TO_CHANNEL__NOT_IN_CHANNEL]!.summary(g),
    howToFixIt: (g) =>
      md(
        ctxOf(g).t('Invite Dado bot to **{{ channel }}** channel', { channel: slackChannelName(g) })
      )
  },
  [IssueKind.SLACK_INVITE_TO_CHANNEL__NO_ACTOR]: {
    category: 'Automation: Slack channel invitation',
    summary: (g) =>
      ctxOf(g).t(`Couldn't invite user - missing supporting actor **{{ actor }}**`, {
        actor: capitalize(ctxOf(g).supportingActors?.[errData(g).data?.actorKey]?.name || errData(g).data?.actorKey)
      }),
    whatCausedIt: (g) =>
      automationLegacyCopy[IssueKind.SLACK_INVITE_TO_CHANNEL__NO_ACTOR]!.summary(g),
    howToFixIt: participantLink
  },
  [IssueKind.SLACK_INVITE_TO_CHANNEL__CHANNEL_NOT_FOUND]: {
    category: 'Automation: Slack channel invitation',
    summary: (g) =>
      ctxOf(g).t(`Couldn't find Slack channel **{{channel}}**`, { channel: slackChannelName(g) }),
    whatCausedIt: (g) =>
      automationLegacyCopy[IssueKind.SLACK_INVITE_TO_CHANNEL__CHANNEL_NOT_FOUND]!.summary(g),
    howToFixIt: (g) =>
      md(ctxOf(g).t('Make sure the channel exists and that the Dado bot is a member of it'))
  },
  [IssueKind.SLACK_INVITE_TO_CHANNEL__DEFAULT]: {
    category: 'Automation: Slack channel invitation',
    summary: (g) => ctxOf(g).t('Unknown error'),
    whatCausedIt: (g) => ctxOf(g).t('Unknown error'),
    howToFixIt: () => null
  },

  // ---- CREATE_DOCUMENT_FROM_TEMPLATE ----
  [IssueKind.CREATE_DOCUMENT_FROM_TEMPLATE__TEMPLATE_NOT_FOUND]: {
    category: 'Automation: Document creation',
    summary: (g) => ctxOf(g).t(`Can't find the source template for document creation`),
    whatCausedIt: (g) => ctxOf(g).t(`Can't find the source template for document creation`),
    howToFixIt: (g) =>
      md(
        ctxOf(g).t(
          'Make sure the template document still exists and the Dado user has the rights to access it and retry.'
        )
      )
  },
  [IssueKind.CREATE_DOCUMENT_FROM_TEMPLATE__TARGET_RESOURCE_MISSING]: {
    category: 'Automation: Document creation',
    summary: (g) => ctxOf(g).t(`The target folder resource is missing`),
    whatCausedIt: (g) => ctxOf(g).t(`The target folder resource is missing`),
    howToFixIt: (g) =>
      md(
        ctxOf(g).t(
          'There is a configuration error in the template. The target folder is missing please contact Dado support. '
        )
      )
  },
  [IssueKind.CREATE_DOCUMENT_FROM_TEMPLATE__UNABLE_TO_CREATE_DOCUMENT]: {
    category: 'Automation: Document creation',
    summary: (g) => ctxOf(g).t(`Unable to create document`),
    whatCausedIt: (g) => ctxOf(g).t(`Unable to create document`),
    howToFixIt: (g) =>
      md(
        ctxOf(g).t(
          'Could not write the file to the target destination. Please make sure the Dado user has the appropriate access rights.'
        )
      )
  },
  [IssueKind.CREATE_DOCUMENT_FROM_TEMPLATE__API_ERROR]: {
    category: 'Automation: Document creation',
    summary: (g) => ctxOf(g).t('{{ provider }} API Error', { provider: documentProviderName(g) }),
    whatCausedIt: (g) => ctxOf(g).t('{{ provider }} API Error', { provider: documentProviderName(g) }),
    howToFixIt: () => null
  },

  // ---- HTTP_POST ----
  [IssueKind.HTTP_POST__UNAUTHORIZED]: {
    category: 'Automation: HTTP POST',
    summary: (g) => ctxOf(g).t('Authentication failed'),
    whatCausedIt: (g) => ctxOf(g).t('Authentication failed'),
    howToFixIt: (g) => md(ctxOf(g).t('Update authentication settings in automation'))
  },
  [IssueKind.HTTP_POST__BAD_REQUEST]: {
    category: 'Automation: HTTP POST',
    summary: (g) => ctxOf(g).t('Request failed due to issues with request body'),
    whatCausedIt: (g) => ctxOf(g).t('Request failed due to issues with request body'),
    howToFixIt: (g) =>
      md(ctxOf(g).t('Check requirements for request body and then update automation'))
  },
  [IssueKind.HTTP_POST__NOT_FOUND]: {
    category: 'Automation: HTTP POST',
    summary: (g) => ctxOf(g).t('The URL specified does not exist'),
    whatCausedIt: (g) => ctxOf(g).t('The URL specified does not exist'),
    howToFixIt: (g) => md(ctxOf(g).t('Update URL in automation'))
  },
  [IssueKind.HTTP_POST__NOT_ALLOWED]: {
    category: 'Automation: HTTP POST',
    summary: (g) => ctxOf(g).t('Post requests not permitted to this URL'),
    whatCausedIt: (g) => ctxOf(g).t('Post requests not permitted to this URL'),
    howToFixIt: (g) => md(ctxOf(g).t('Use a different URL in automation'))
  },
  [IssueKind.HTTP_POST__TIMEOUT]: {
    category: 'Automation: HTTP POST',
    summary: (g) => ctxOf(g).t('Error communicating with this URL'),
    whatCausedIt: (g) => ctxOf(g).t('Error communicating with this URL'),
    howToFixIt: (g) => md(ctxOf(g).t('Retry after a few minutes'))
  },
  [IssueKind.HTTP_POST__DEFAULT]: {
    category: 'Automation: HTTP POST',
    summary: (g) =>
      ctxOf(g).t('Error communicating with this URL ({{statusCode}})', {
        statusCode: errData(g)?.statusCode || 'UNKNOWN'
      }),
    whatCausedIt: (g) => automationLegacyCopy[IssueKind.HTTP_POST__DEFAULT]!.summary(g),
    howToFixIt: (g) => md(ctxOf(g).t('Contact Support for help'))
  },

  // ---- DOCEBO_ENROLL ----
  // Ported from the docebo branch's getActionErrorDetailsText DOCEBO_ENROLL
  // cascade (whatCausedIt). The original cascade had no SuggestedActionForError
  // branch for docebo, so howToFixIt is null — matching that fallthrough.
  // Requires @dadohr/shared to export AutomationActionType.DOCEBO_ENROLL.
  [IssueKind.DOCEBO_ENROLL__NO_USER]: {
    category: 'Automation: Docebo enrollment',
    summary: (g) => ctxOf(g).t('No Docebo user found for the assignee'),
    whatCausedIt: (g) => ctxOf(g).t('No Docebo user found was found for the assignee'),
    howToFixIt: (g) => md(ctxOf(g).t('Create the user in Docebo, or correct their email address'))
  },
  [IssueKind.DOCEBO_ENROLL__NO_ACTOR]: {
    category: 'Automation: Docebo enrollment',
    summary: (g) => {
      const c = ctxOf(g);
      const actorKey = errData(g)?.data?.actorKey;
      return c.t(`Couldn't add to course - **{{ actor }}** not set for this experience`, {
        actor: capitalize(c.supportingActors?.[actorKey]?.name || actorKey)
      });
    },
    whatCausedIt: (g) => automationLegacyCopy[IssueKind.DOCEBO_ENROLL__NO_ACTOR]!.summary(g),
    howToFixIt: () => null
  },
  [IssueKind.DOCEBO_ENROLL__NO_EMAIL]: {
    category: 'Automation: Docebo enrollment',
    summary: (g) => {
      const c = ctxOf(g);
      const ed = errData(g);
      const actor = humanReadableActorName(g, ed?.data?.actorKey);
      if (ed?.data?.emailField === 'email')
        return c.t(`Couldn't add to course - **{{actor}}** missing **Work Email**`, { actor });
      if (ed?.data?.emailField === 'personalEmail')
        return c.t(`Couldn't add to course - **{{actor}}** missing **Personal Email**`, { actor });
      return c.t('Unknown error');
    },
    whatCausedIt: (g) => automationLegacyCopy[IssueKind.DOCEBO_ENROLL__NO_EMAIL]!.summary(g),
    howToFixIt: () => null
  },
  [IssueKind.DOCEBO_ENROLL__DEFAULT]: {
    category: 'Automation: Docebo enrollment',
    summary: (g) => ctxOf(g).t('Unknown error'),
    whatCausedIt: (g) => ctxOf(g).t('Unknown error'),
    howToFixIt: () => null
  },

  // ---- LESSONLY_ASSIGN_TO_CONTENT ----
  // Mirrors DOCEBO_ENROLL above, "Docebo"→"Lessonly", "course"→"content".
  [IssueKind.LESSONLY_ASSIGN__NO_USER]: {
    category: 'Automation: Lessonly assignment',
    summary: (g) => ctxOf(g).t('No Lessonly user found for the assignee'),
    whatCausedIt: (g) => ctxOf(g).t('No Lessonly user was found for the assignee'),
    howToFixIt: (g) => md(ctxOf(g).t('Create the user in Lessonly, or correct their email address'))
  },
  [IssueKind.LESSONLY_ASSIGN__NO_ACTOR]: {
    category: 'Automation: Lessonly assignment',
    summary: (g) => {
      const c = ctxOf(g);
      const actorKey = errData(g)?.data?.actorKey;
      if (!actorKey) return c.t(`Couldn't assign content - an assignee is not set for this experience`);
      return c.t(`Couldn't assign content - **{{ actor }}** not set for this experience`, {
        actor: capitalize(c.supportingActors?.[actorKey]?.name || actorKey)
      });
    },
    whatCausedIt: (g) => automationLegacyCopy[IssueKind.LESSONLY_ASSIGN__NO_ACTOR]!.summary(g),
    howToFixIt: () => null
  },
  [IssueKind.LESSONLY_ASSIGN__NO_EMAIL]: {
    category: 'Automation: Lessonly assignment',
    summary: (g) => {
      const c = ctxOf(g);
      const ed = errData(g);
      const actorKey = ed?.data?.actorKey;
      if (!actorKey) return c.t(`Couldn't assign content - an assignee has no email address`);
      const actor = humanReadableActorName(g, actorKey);
      if (ed?.data?.emailField === 'email')
        return c.t(`Couldn't assign content - **{{actor}}** missing **Work Email**`, { actor });
      if (ed?.data?.emailField === 'personalEmail')
        return c.t(`Couldn't assign content - **{{actor}}** missing **Personal Email**`, { actor });
      return c.t(`Couldn't assign content - an assignee has no email address`);
    },
    whatCausedIt: (g) => automationLegacyCopy[IssueKind.LESSONLY_ASSIGN__NO_EMAIL]!.summary(g),
    howToFixIt: () => null
  },
  [IssueKind.LESSONLY_ASSIGN__DEFAULT]: {
    category: 'Automation: Lessonly assignment',
    summary: (g) => ctxOf(g).t('Unknown error'),
    whatCausedIt: (g) => ctxOf(g).t('Unknown error'),
    howToFixIt: () => null
  },

  // ---- UNENROLL_FROM_EXPERIENCE ----
  [IssueKind.UNENROLL_FROM_EXPERIENCE]: {
    category: 'Automation: Unenrollment',
    summary: (g) => ctxOf(g).t('Failed to archive this experience instance'),
    whatCausedIt: (g) => ctxOf(g).t('Failed to archive this experience instance'),
    howToFixIt: () => null
  },

  // ---- SYNC_TO_DATA_DESTINATION ----
  // Ported from dev's SYNC_TO_DATA_DESTINATION branches added to the cascades
  // (getActionErrorDetailsText → whatCausedIt, SuggestedActionForError →
  // howToFixIt) during the dev merge. Verbatim, per Phase 1 discipline.
  [IssueKind.SYNC_TO_DATA_DESTINATION]: {
    category: 'Automation: Data destination sync',
    summary: (g) =>
      ctxOf(g).t('We could not sync the participant data to the data destination. {{detail}}', {
        detail: errData(g)?.type || errData(g)?.message || ''
      }),
    whatCausedIt: (g) => automationLegacyCopy[IssueKind.SYNC_TO_DATA_DESTINATION]!.summary(g),
    howToFixIt: (g) =>
      md(
        ctxOf(g).t(
          'Check the data destination configuration (the destination, its resource, and the field mappings) and confirm the integration is still connected, then let the automation re-run.'
        )
      )
  },

  // ---- WORKDAY_TRIGGER_BUSINESS_PROCESS ----
  // errorData.message is the writer's "<CODE>: <detail>" (see
  // external-operations-worker workdayBusinessProcess.ts); the detail is shown,
  // the code picks the fix.
  [IssueKind.WORKDAY_TRIGGER_BUSINESS_PROCESS]: {
    category: 'Automation: Workday business process',
    summary: (g) => {
      const detail = workdayErrorDetail(errData(g)?.message);
      return detail
        ? ctxOf(g).t('Workday update failed: {{detail}}', { detail })
        : ctxOf(g).t('Workday update failed');
    },
    whatCausedIt: (g) => automationLegacyCopy[IssueKind.WORKDAY_TRIGGER_BUSINESS_PROCESS]!.summary(g),
    howToFixIt: (g) => {
      const t = ctxOf(g).t;
      const code = workdayErrorCode(errData(g)?.message);
      if (code === 'WORKDAY_BP_REFERENCE_NOT_FOUND' || code === 'WORKDAY_BP_REFERENCE_AMBIGUOUS')
        return md(t("Set the participant's field to the Workday ID or the exact Workday name, then retry."));
      if (code === 'WORKDAY_BP_MISSING_VALUE' || code === 'WORKDAY_BP_INVALID_AMOUNT' || code === 'WORKDAY_BP_START_DATE_EMPTY')
        return md(t("Fill in the participant's mapped field, then retry."));
      if (code === 'WORKDAY_BP_MISSING_SETTING')
        return md(t('Complete the business process settings in the automation, then retry.'));
      if (code === 'WORKDAY_WORKER_ID_NOT_FOUND')
        return md(t("The participant has no Workday ID — check the Workday import's field mapping, then retry."));
      if (code === 'WORKDAY_BP_EMPLOYEE_ONLY') return null;
      return md(t('Check the Workday integration connection and permissions, then retry.'));
    }
  }
};

/** "CODE: detail" → "CODE" (undefined when the message has no code prefix). */
function workdayErrorCode(message: unknown): string | undefined {
  return typeof message === 'string' ? /^([A-Z0-9_]+)\b/.exec(message)?.[1] : undefined;
}

/** "CODE: detail" → "detail"; a bare code or non-string → ''. */
function workdayErrorDetail(message: unknown): string {
  if (typeof message !== 'string') return '';
  const i = message.indexOf(': ');
  return i >= 0 ? message.slice(i + 2) : '';
}
